/**
 * Socket.io Context
 * Manages real-time communication with backend for bids, notifications, and messaging
 * Provides useSocket hook for accessing socket functionality throughout the application
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

/**
 * SocketProvider component
 * Establishes and manages Socket.io connection
 */
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [liveRFPUpdates, setLiveRFPUpdates] = useState({});
  const [activeRFPRoom, setActiveRFPRoom] = useState(null);
  const { user } = useAuth();

  // Initialize socket connection
  useEffect(() => {
    if (!user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    const token = sessionStorage.getItem('authToken');
    const newSocket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000', {
      auth: {
        token,
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    // Connection event handlers
    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Bid submission event handler
    newSocket.on('bid_submitted', (bidData) => {
      setLiveRFPUpdates((prev) => ({
        ...prev,
        [bidData.rfpId]: {
          ...prev[bidData.rfpId],
          bidCount: (prev[bidData.rfpId]?.bidCount || 0) + 1,
          latestBid: bidData,
        },
      }));

      addNotification({
        type: 'bid_submitted',
        title: 'New Bid Received',
        message: `A new bid was submitted for ${bidData.projectTitle}`,
        data: bidData,
      });
    });

    // RFP update event handler
    newSocket.on('rfp_updated', (rfpData) => {
      setLiveRFPUpdates((prev) => ({
        ...prev,
        [rfpData.id]: rfpData,
      }));
    });

    // New message event handler
    newSocket.on('new_message', (messageData) => {
      addNotification({
        type: 'new_message',
        title: 'New Message',
        message: `New message from ${messageData.senderName}`,
        data: messageData,
      });
    });

    // General notification event handler
    newSocket.on('notification', (notificationData) => {
      addNotification(notificationData);
    });

    // Payment confirmation event handler
    newSocket.on('payment_confirmed', (paymentData) => {
      addNotification({
        type: 'payment_confirmed',
        title: 'Payment Confirmed',
        message: `Payment of $${paymentData.amount} has been confirmed`,
        data: paymentData,
      });
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user]);

  /**
   * Add notification to queue
   */
  const addNotification = useCallback((notif) => {
    const id = Date.now();
    const notification = {
      id,
      timestamp: new Date(),
      read: false,
      ...notif,
    };

    setNotifications((prev) => [notification, ...prev].slice(0, 50)); // Keep last 50
  }, []);

  /**
   * Mark notification as read
   */
  const markNotificationRead = useCallback((notificationId) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
  }, []);

  /**
   * Clear notification
   */
  const clearNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId));
  }, []);

  /**
   * Join RFP room for real-time updates
   */
  const joinRFPRoom = useCallback(
    (rfpId) => {
      if (socket) {
        socket.emit('join_rfp_room', { rfpId });
        setActiveRFPRoom(rfpId);
      }
    },
    [socket]
  );

  /**
   * Leave RFP room
   */
  const leaveRFPRoom = useCallback(
    (rfpId) => {
      if (socket) {
        socket.emit('leave_rfp_room', { rfpId });
        setActiveRFPRoom(null);
      }
    },
    [socket]
  );

  /**
   * Send message in real-time
   */
  const sendMessage = useCallback(
    (messageData) => {
      if (socket && isConnected) {
        socket.emit('send_message', messageData);
      }
    },
    [socket, isConnected]
  );

  /**
   * Submit bid notification to other users
   */
  const notifyBidSubmission = useCallback(
    (bidData) => {
      if (socket && isConnected) {
        socket.emit('bid_submitted', bidData);
      }
    },
    [socket, isConnected]
  );

  /**
   * Request payment confirmation
   */
  const requestPaymentConfirmation = useCallback(
    (paymentData) => {
      if (socket && isConnected) {
        socket.emit('payment_confirmation_request', paymentData);
      }
    },
    [socket, isConnected]
  );

  const value = {
    socket,
    isConnected,
    notifications,
    liveRFPUpdates,
    activeRFPRoom,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotification,
    joinRFPRoom,
    leaveRFPRoom,
    sendMessage,
    notifyBidSubmission,
    requestPaymentConfirmation,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

/**
 * Custom hook to use socket context
 * Must be called within component wrapped by SocketProvider
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export default SocketContext;
